import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { isTokenExpiredOrExpiring } from './tokenService';

export interface ApiClientConfig {
  baseURL: string;
  // Default raised from 5 000 ms → 30 000 ms.
  // 5 s was far too short: the /access endpoint calls the database AND SDA APIs
  // which can take 5–15 s under 650-user load. Requests timed out before the
  // backend could respond, showing a blank/error screen after successful login.
  timeout?: number;
  getAccessToken: () => string | undefined;
  refreshTokenCallback: () => Promise<boolean>;
  onTokenRefreshFailed?: () => void;
}

/**
 * Creates an API client with automatic token refresh functionality
 */
export const createApiClient = (config: ApiClientConfig): AxiosInstance => {
  const { baseURL, timeout = 30000, getAccessToken, refreshTokenCallback, onTokenRefreshFailed } = config;

  const apiClient = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Include credentials for session management
  });

  // Request interceptor to add auth token and handle token refresh
  apiClient.interceptors.request.use(
    async (requestConfig: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
      const accessToken = getAccessToken();
      
      // Check if token needs refresh before making the request
      if (accessToken && isTokenExpiredOrExpiring(accessToken, 5)) {
        try {
          const refreshSuccess = await refreshTokenCallback();
          if (refreshSuccess) {
            // Get the new token after refresh
            const newToken = getAccessToken();
            if (newToken) {
              requestConfig.headers = requestConfig.headers || {};
              requestConfig.headers['Authorization'] = `Bearer ${newToken}`;
            }
          } else {
            // Continue with existing token even if refresh failed
            requestConfig.headers = requestConfig.headers || {};
            requestConfig.headers['Authorization'] = `Bearer ${accessToken}`;
          }
        } catch (error) {
          // Continue with existing token even if refresh errored
          requestConfig.headers = requestConfig.headers || {};
          requestConfig.headers['Authorization'] = `Bearer ${accessToken}`;
        }
      } else if (accessToken) {
        // Use existing token if it's still valid
        requestConfig.headers = requestConfig.headers || {};
        requestConfig.headers['Authorization'] = `Bearer ${accessToken}`;
      } 
      return requestConfig;
    },
    (error) => {
      console.error('[API-CLIENT] Request interceptor error:', {
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
        time: new Date().toISOString(),
      });
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle 401 errors and retry with refreshed token
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Handle 401 errors (unauthorized)
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        
        try {
          const refreshSuccess = await refreshTokenCallback();
          if (refreshSuccess) {
            // Update the authorization header with the new token
            const newToken = getAccessToken();
            if (newToken) {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return apiClient(originalRequest);
            }
          } else {
            if (onTokenRefreshFailed) {
              onTokenRefreshFailed();
            }
          }
        } catch (refreshError) {
          if (onTokenRefreshFailed) {
            onTokenRefreshFailed();
          }
        }
      }

      // Let the error bubble up to be handled by the calling function
      return Promise.reject(error);
    }
  );

  return apiClient;
};